import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';

const ServicesPreview = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const services = [
    {
      id: 'ai-image',
      title: 'AI Image Generation',
      description: 'Generate high‑quality images in seconds. Lower cost and faster delivery for your content needs.',
      icon: 'ri-image-line',
      image: 'https://public.readdy.ai/ai/img_res/2e4b5c92d470a44e483e5bc9ce77a850.jpg',
      link: '/services/ai-image-generation'
    },
    {
      id: 'ai-video',
      title: 'AI Video Generation',
      description: 'Compelling, high‑definition videos that capture attention. No expensive film crew required.',
      icon: 'ri-film-line',
      image: 'https://readdy.ai/api/search-image?query=Cinematic%20video%20production%20setup%20with%20AI%20technology%2C%20professional%20camera%20equipment%2C%20hot%20pink%20and%20aqua%20lighting%20effects%2C%20movie%20production%20studio%20atmosphere%2C%20dramatic%20lighting%2C%20high%20quality%20photography%2C%208k%20resolution&width=600&height=800&seq=service-vid-1&orientation=portrait',
      link: '/services/ai-video-generation'
    },
    {
      id: 'hybrid-content',
      title: 'AI Content Enhancement',
      description: 'Clean up audio, improve visuals and optimize your content to breathe new life into campaigns.',
      icon: 'ri-magic-line',
      image: 'https://readdy.ai/api/search-image?query=Hybrid%20digital%20content%20creation%20showing%20blend%20of%20images%20and%20video%2C%20hot%20pink%20and%20aqua%20neon%20color%20scheme%2C%20futuristic%20creative%20workspace%2C%20holographic%20displays%2C%20professional%20studio%20lighting%2C%20ultra%20high%20quality%2C%208k%20detail&width=600&height=800&seq=service-hyb-1&orientation=portrait',
      link: '/services/hybrid-content'
    }
  ];

  // Header animation - fades out and moves up as cards appear
  const headerOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);
  const headerY = useTransform(scrollYProgress, [0, 0.15], [0, -100]);

  return (
    <section id="services" ref={containerRef} className="relative bg-black">
      <div className="h-screen sticky top-0 flex items-center justify-center">
        <div className="max-w-[1400px] w-full mx-auto px-4 sm:px-6 lg:px-12">
          {/* Section Header - Animates out */}
          <motion.div
            style={{
              opacity: headerOpacity,
              y: headerY
            }}
            className="text-center mb-6 md:mb-12"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light mb-3 md:mb-4">
              <span className="text-white">Our </span>
              <span className="bg-gradient-to-r from-brand-pink to-brand-aqua bg-clip-text text-transparent">Services</span>
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-white/60 max-w-2xl mx-auto px-4">
              Explore our suite of AI-powered creative solutions
            </p>
          </motion.div>
        </div>
      </div>

      {/* Scrolling Content Area */}
      <div className="relative" style={{ height: '400vh' }}>
        <div className="sticky top-0 h-screen flex items-center overflow-hidden">
          <div className="max-w-[1400px] w-full mx-auto px-4 sm:px-6 lg:px-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
              {services.map((service, index) => {
                // Each card appears sequentially from left to right
                const cardProgress = useTransform(
                  scrollYProgress,
                  [0.1 + index * 0.15, 0.25 + index * 0.15],
                  [0, 1]
                );
                
                const x = useTransform(cardProgress, [0, 1], [100, 0]);
                const opacity = useTransform(cardProgress, [0, 0.5, 1], [0, 0.5, 1]);

                // Stack cards to the right
                const stackProgress = useTransform(
                  scrollYProgress,
                  [0.65, 0.8 + index * 0.05],
                  [0, 1]
                );
                
                const stackX = useTransform(stackProgress, [0, 1], [0, 400 + index * 100]);
                const stackY = useTransform(stackProgress, [0, 1], [0, -200 + index * 250]);
                const stackScale = useTransform(stackProgress, [0, 1], [1, 0.7]);

                return (
                  <motion.div
                    key={service.id}
                    style={{ 
                      x: useTransform(() => x.get() + stackX.get() + '%'),
                      y: stackY,
                      opacity,
                      scale: stackScale
                    }}
                    className="relative"
                  >
                    <Link to={service.link}>
                      <motion.div
                        whileHover={{ y: -10 }}
                        className="relative bg-gradient-to-br from-zinc-900 to-black rounded-lg sm:rounded-xl overflow-hidden border border-white/10 cursor-pointer group h-[420px] sm:h-[460px] lg:h-[500px]"
                      >
                        {/* Image */}
                        <div className="relative h-[180px] sm:h-[200px] lg:h-[220px] overflow-hidden">
                          <img
                            src={service.image}
                            alt={service.title}
                            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/80" />
                        </div>

                        {/* Content */}
                        <div className="relative p-4 sm:p-5 lg:p-6">
                          <div className="flex items-start gap-2 sm:gap-3 mb-3">
                            <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-brand-pink/20 to-brand-aqua/20 border border-brand-pink/30 flex-shrink-0">
                              <i className={`${service.icon} text-lg sm:text-xl text-brand-pink`}></i>
                            </div>
                            <h3 className="text-lg sm:text-xl lg:text-2xl font-light text-white leading-tight">
                              {service.title}
                            </h3>
                          </div>
                          
                          <p className="text-sm sm:text-base text-white/70 mb-4 leading-relaxed">
                            {service.description}
                          </p>

                          <motion.div
                            whileHover={{ x: 10 }}
                            className="flex items-center gap-2 text-brand-aqua font-semibold text-sm"
                          >
                            <span>Learn More</span>
                            <i className="ri-arrow-right-line text-base"></i>
                          </motion.div>
                        </div>
                      </motion.div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>

            {/* Summary Text - Appears on Left - Hidden on mobile */}
            <motion.div
              style={{
                opacity: useTransform(scrollYProgress, [0.8, 0.88], [0, 1]),
                x: useTransform(scrollYProgress, [0.8, 0.88], [-100, 0])
              }}
              className="hidden lg:block absolute left-12 top-1/2 -translate-y-1/2 max-w-xl"
            >
              <h3 className="text-4xl font-light text-white mb-5">
                <span className="bg-gradient-to-r from-brand-pink to-brand-aqua bg-clip-text text-transparent">
                  Comprehensive
                </span>
                <br />
                Creative Solutions
              </h3>
              <p className="text-base text-white/70 leading-relaxed mb-8">
                 Stay ahead of your competition by producing content faster. We work with you to save time and cost using the newest generative AI models. Contact us to find out how we can help future-proof your content.   
              </p>
              <Link to="/services">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-7 py-3 bg-gradient-to-r from-brand-pink to-brand-aqua text-white font-semibold rounded-full whitespace-nowrap cursor-pointer text-sm"
                >
                  Explore All Services
                </motion.button>
              </Link>
            </motion.div>

            {/* Animated Image - Appears on Right - Hidden on mobile */}
            <motion.div
              style={{
                opacity: useTransform(scrollYProgress, [0.85, 0.92], [0, 1]),
                scale: useTransform(scrollYProgress, [0.85, 0.92], [0.8, 1]),
                rotate: useTransform(scrollYProgress, [0.85, 0.92], [10, 0])
              }}
              className="hidden lg:block absolute right-12 top-1/2 -translate-y-1/2 w-[450px] h-[450px]"
            >
              <div className="relative w-full h-full rounded-2xl overflow-hidden border-2 border-brand-pink/30 shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20business%20consultation%20scene%20showing%20creative%20consultant%20and%20client%20collaborating%20together%20at%20modern%20desk%20with%20large%20monitor%20displaying%20creative%20content%20and%20AI-generated%20visuals%2C%20both%20people%20engaged%20in%20friendly%20discussion%20pointing%20at%20screen%2C%20consultant%20explaining%20ideas%20while%20client%20listens%20attentively%2C%20contemporary%20office%20setting%20with%20natural%20window%20lighting%2C%20warm%20professional%20atmosphere%2C%20diverse%20team%20working%20on%20creative%20project%20together%2C%20laptop%20and%20design%20materials%20on%20desk%2C%20collaborative%20problem-solving%20moment%2C%20high-quality%20corporate%20photography%20with%20shallow%20depth%20of%20field%2C%20modern%20workspace%20with%20plants%20and%20minimalist%20decor%2C%20authentic%20business%20partnership%20and%20teamwork%2C%20photorealistic%208k%20quality%20with%20soft%20natural%20lighting&width=500&height=500&seq=consultant-client-collaboration-v2&orientation=squarish"
                  alt="Black Velvet Consultant and Client Collaboration"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-transparent to-black/30" />
              </div>
            </motion.div>

            {/* Mobile Summary Section - Shows after cards on mobile/tablet */}
            <motion.div
              style={{
                opacity: useTransform(scrollYProgress, [0.8, 0.88], [0, 1]),
              }}
              className="lg:hidden text-center px-4 sm:px-6 mt-6"
            >
              <h3 className="text-2xl sm:text-3xl font-light text-white mb-3">
                <span className="bg-gradient-to-r from-brand-pink to-brand-aqua bg-clip-text text-transparent">
                  Comprehensive
                </span>
                <br />
                Creative Solutions
              </h3>
              <p className="text-sm sm:text-base text-white/70 leading-relaxed mb-6 max-w-2xl mx-auto">
                 Stay ahead of your competition by producing content faster. We work with you to save time and cost using the newest generative AI models. Contact us to find out how we can help future-proof your content.   
              </p>
              <Link to="/services">
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  className="px-6 sm:px-7 py-3 bg-gradient-to-r from-brand-pink to-brand-aqua text-white font-semibold rounded-full whitespace-nowrap cursor-pointer text-sm"
                >
                  Explore All Services
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesPreview;
